import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Search, 
  MapPin,
  Heart,
  MessageCircle,
  TrendingUp,
  X,
  User
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { debounce } from '@/lib/utils';
import BottomNav from '@/components/BottomNav';

const SearchPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { api } = useAuth();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [activeTab, setActiveTab] = useState('posts');
  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch trending on mount
  useEffect(() => {
    fetchTrending();
  }, []);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce(async (searchQuery) => {
      if (!searchQuery.trim() || searchQuery.length < 2) {
        setPosts([]);
        setUsers([]);
        return;
      }

      setLoading(true);
      try {
        const [postsRes, usersRes] = await Promise.all([
          api.get('/posts', { params: { search: searchQuery } }),
          api.get(`/users/search/${searchQuery}`)
        ]);
        setPosts(postsRes.data);
        setUsers(usersRes.data);
      } catch (error) {
        toast.error('Erreur lors de la recherche');
      } finally {
        setLoading(false);
      }
    }, 300),
    [api]
  );

  useEffect(() => {
    debouncedSearch(query);
  }, [query, debouncedSearch]);

  const fetchTrending = async () => {
    try {
      const response = await api.get('/posts/trending');
      setTrending(response.data);
    } catch (error) {
      console.error('Error fetching trending:', error);
    }
  };

  const handleSearch = (value) => {
    setQuery(value);
    if (value) {
      setSearchParams({ q: value });
    } else {
      setSearchParams({});
    }
  };

  const getCategoryStyle = (category) => {
    const styles = {
      alerte: 'badge-alerte',
      evenement: 'badge-evenement',
      bon_plan: 'badge-bon_plan',
      info: 'badge-info'
    };
    return styles[category] || '';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      alerte: 'Alerte',
      evenement: 'Événement',
      bon_plan: 'Bon Plan',
      info: 'Info'
    };
    return labels[category] || category;
  };

  return (
    <div className="min-h-screen tropic-bg pb-28 md:pb-12 md:pt-16">
      {/* Header */}
      <header className="sticky top-0 md:top-16 z-40 glass border-b">
        <div className="container mx-auto max-w-2xl px-4 py-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher posts, utilisateurs..."
                className="pl-10 pr-10"
                value={query}
                onChange={(e) => handleSearch(e.target.value)}
                autoFocus
                data-testid="search-input"
              />
              {query && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                  onClick={() => handleSearch('')}
                  data-testid="clear-search-btn"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto max-w-2xl px-4 py-6">
        {!query ? (
          // Show trending when no search query
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-bold flex items-center gap-2 mb-4">
                <TrendingUp className="h-5 w-5 text-primary" />
                Tendances du jour
              </h2>
              {trending.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  Aucune tendance pour le moment
                </p>
              ) : (
                <div className="space-y-4">
                  {trending.map((post, index) => (
                    <Card 
                      key={post.post_id}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => navigate(`/post/${post.post_id}`)}
                      data-testid={`trending-post-${post.post_id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <span className="text-2xl font-bold text-muted-foreground">
                            {index + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <Badge className={`${getCategoryStyle(post.category)} text-xs mb-2`}>
                              {getCategoryLabel(post.category)}
                            </Badge>
                            <h3 className="font-bold line-clamp-2">{post.title}</h3>
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Heart className="h-3 w-3" /> {post.likes_count}
                              </span>
                              <span className="flex items-center gap-1">
                                <MessageCircle className="h-3 w-3" /> {post.comments_count}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" /> {post.commune}
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          // Show search results
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="posts" data-testid="tab-posts">
                Posts ({posts.length})
              </TabsTrigger>
              <TabsTrigger value="users" data-testid="tab-users">
                Utilisateurs ({users.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="posts">
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="skeleton h-24 rounded-xl" />
                  ))}
                </div>
              ) : posts.length === 0 ? (
                <div className="text-center py-12">
                  <Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucun post trouvé</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {posts.map((post) => (
                    <Card 
                      key={post.post_id}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => navigate(`/post/${post.post_id}`)}
                      data-testid={`search-post-${post.post_id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <Avatar className="h-10 w-10 flex-shrink-0">
                            <AvatarImage src={post.user_picture} />
                            <AvatarFallback>{post.user_name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-sm">{post.user_name}</span>
                              <Badge className={`${getCategoryStyle(post.category)} text-xs`}>
                                {getCategoryLabel(post.category)}
                              </Badge>
                            </div>
                            <h3 className="font-bold line-clamp-1">{post.title}</h3>
                            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                              {post.content}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Heart className="h-3 w-3" /> {post.likes_count}
                              </span>
                              <span className="flex items-center gap-1">
                                <MessageCircle className="h-3 w-3" /> {post.comments_count}
                              </span>
                              <span>
                                {formatDistanceToNow(new Date(post.created_at), { addSuffix: true, locale: fr })}
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="users">
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="skeleton h-20 rounded-xl" />
                  ))}
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-12">
                  <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucun utilisateur trouvé</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {users.map((searchUser) => (
                    <Card 
                      key={searchUser.user_id}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => navigate(`/profile/${searchUser.user_id}`)}
                      data-testid={`search-user-${searchUser.user_id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <Avatar className="h-14 w-14">
                            <AvatarImage src={searchUser.picture} />
                            <AvatarFallback className="text-lg">{searchUser.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <h3 className="font-bold">{searchUser.name}</h3>
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {searchUser.commune}
                            </p>
                            <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                              <span>{searchUser.followers_count || 0} followers</span>
                              <span>{searchUser.posts_count || 0} posts</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </main>

      <BottomNav />
    </div>
  );
};

export default SearchPage;
